/**
 * reachability: entry-point — which Sarah launcher clip plays, by chat event.
 * Pure so every transition is unit tested. Draft (Front Desk checks clips first).
 *
 * Slots: wave (once on load), stepoff (while Sarah is thinking), nod (while the
 * visitor types), point (after SARAH_POINT_IDLE_MS with no clicks, rate
 * limited), thumbsup (once after an answer arrives), resting (default loop).
 * Every one-shot returns to resting. Continuous states (stepoff, nod) win over
 * one-shots; a one-shot that is already playing finishes unless a continuous
 * state starts.
 */
export type SarahClipSlot = 'wave' | 'stepoff' | 'nod' | 'point' | 'thumbsup' | 'resting'
export type SarahOneShot = 'wave' | 'point' | 'thumbsup'

export const SARAH_ONE_SHOTS: readonly SarahOneShot[] = ['wave', 'point', 'thumbsup']
/** No clicks for this long plays the point clip. */
export const SARAH_POINT_IDLE_MS = 20_000
/** At most this many point plays per session. */
export const SARAH_POINT_MAX_PLAYS = 1

export type SarahClipSignals = {
  thinking: boolean
  typing: boolean
  /** A typed question is awaiting Sarah's answer; plays stepoff like thinking. */
  pending?: boolean
}

export type SarahClipState = {
  slot: SarahClipSlot
  waved: boolean
  pointPlays: number
}

export type SarahClipEvent =
  | { type: 'load' }
  | { type: 'signals'; signals: SarahClipSignals }
  | { type: 'idle' }
  | { type: 'answer' }
  | { type: 'ended' }
  | { type: 'failed' }

export function initialSarahClipState(waved = false, pointPlays = 0): SarahClipState {
  return { slot: 'resting', waved, pointPlays }
}

function isOneShot(slot: SarahClipSlot): slot is SarahOneShot {
  return (SARAH_ONE_SHOTS as readonly string[]).includes(slot)
}

function busy(s: SarahClipState): boolean {
  return s.slot === 'stepoff' || s.slot === 'nod' || isOneShot(s.slot)
}

export function sarahClipReducer(s: SarahClipState, e: SarahClipEvent): SarahClipState {
  switch (e.type) {
    case 'load':
      return s.waved || busy(s) ? s : { ...s, slot: 'wave', waved: true }
    case 'signals': {
      const want: SarahClipSlot | null = e.signals.thinking || e.signals.pending ? 'stepoff' : e.signals.typing ? 'nod' : null
      if (want) return s.slot === want ? s : { ...s, slot: want }
      // Continuous state ended: back to resting. One-shots keep playing.
      return s.slot === 'stepoff' || s.slot === 'nod' ? { ...s, slot: 'resting' } : s
    }
    case 'idle':
      if (busy(s) || s.pointPlays >= SARAH_POINT_MAX_PLAYS) return s
      return { ...s, slot: 'point', pointPlays: s.pointPlays + 1 }
    case 'answer':
      // Thumbs up replaces the thinking step-off once the reply lands.
      if (s.slot === 'nod' || isOneShot(s.slot)) return s
      return { ...s, slot: 'thumbsup' }
    case 'ended':
    case 'failed':
      return isOneShot(s.slot) ? { ...s, slot: 'resting' } : s
  }
}

/** Continuous slots loop; one-shots play once and fire 'ended'. */
export function sarahClipLoops(slot: SarahClipSlot): boolean {
  return !isOneShot(slot)
}
